import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Grid,
    Stack,
    Autocomplete,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getStation, updateStation } from "../../api/station";
import { Close, Save, Lock, LockOpen } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Landmark } from "../../types/api";
import { listLandmarks } from "../../api/landmark";

const amenityOptions = [
    { key: "info", label: "จุดประชาสัมพันธ์" },
    { key: "buddha", label: "พระพุทธรูป" },
    { key: "market", label: "ตลาดนัด" },
    { key: "souvenir", label: "ร้านจำหน่ายของที่ระลึก" },
    { key: "toilet", label: "ห้องน้ำ" },
    { key: "ticket", label: "อาคารจำหน่ายบัตร" },
    { key: "firstAid", label: "จุดปฐมพยาบาล" },
    { key: "shop", label: "ร้านค้า" },
    { key: "bicycle", label: "จุดบริการจักรยาน" },
    { key: "coffee", label: "ร้านกาแฟ" },
    { key: "royalProject", label: "ร้านอาหารโครงการหลวง" },
    { key: "hotel", label: "โรงแรม" },
];

// ✅ ตั้งค่า icon มาตรฐานของ Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ✅ component สำหรับ fly และ focus map
function FlyToLocation({ position }: { position: [number, number] }) {
    const map = useMap();
    useEffect(() => {
        if (position) {
            map.flyTo(position, map.getZoom(), { animate: true });
            map.invalidateSize();
        }
    }, [position, map]);
    return null;
}

// ✅ component สำหรับ handle click บน map
function MapClickHandler({ setMarker }: { setMarker: (pos: [number, number]) => void }) {
    useMapEvents({
        click(e) {
            const pos: [number, number] = [e.latlng.lat, e.latlng.lng];
            setMarker(pos);
        },
    });
    return null;
}

export default function StationEditPage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [nameTh, setNameTh] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [nameZh, setNameZh] = useState("");
    const [latInput, setLatInput] = useState("");
    const [lngInput, setLngInput] = useState("");
    const [markerPos, setMarkerPos] = useState<[number, number]>([13.75, 100.5]);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
    const [landmarkOptions, setLandmarkOptions] = useState<Landmark[]>([]);
    const [selectedLandmarks, setSelectedLandmarks] = useState<Landmark[]>([]);
    const [amenities, setAmenities] = useState<Record<string, boolean>>({});
    const [viewOnly, setViewOnly] = useState(true); // ✅ toggle สำหรับโหมดดูอย่างเดียว

    useEffect(() => {
        const load = async () => {
            if (!id) return;
            try {
                const station = await getStation(Number(id));
                setNameTh(station.nameTh);
                setNameEn(station.nameEn);
                setNameZh(station.nameZh);
                setLatInput(station.latitude.toString());
                setLngInput(station.longitude.toString());
                setMarkerPos([station.latitude, station.longitude]);
                setFlyTarget([station.latitude, station.longitude]);

                const landmarks = await listLandmarks();
                setLandmarkOptions(landmarks);

                if (station.landmarks) {
                    setSelectedLandmarks(
                        landmarks.filter((lm) =>
                            station.landmarks!.some((sLm) => sLm.id === lm.id)
                        )
                    );
                }

                if (station.amenities) {
                    setAmenities(station.amenities);
                }
            } catch (err: any) {
                alert("โหลดข้อมูลไม่สำเร็จ: " + err.message);
            }
        };
        load();
    }, [id]);

    const handleSubmit = async () => {
        try {
            if (!id) return;
            await updateStation(Number(id), {
                nameTh,
                nameEn,
                nameZh,
                latitude: parseFloat(latInput),
                longitude: parseFloat(lngInput),
                landmarkIds: selectedLandmarks.map((lm) => lm.id),
                amenities,
            });
            alert("แก้ไขสถานีเรียบร้อย ✅");
            navigate("/stations");
        } catch (err: any) {
            alert("แก้ไขล้มเหลว: " + err.message);
        }
    };

    const toggleAmenity = (key: string) => {
        setAmenities((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Box>
            <Typography variant="h6" mb={2}>
                แก้ไขสถานี
            </Typography>
            <Paper sx={{ p: 3 }}>
                <Stack gap={3}>
                    <Grid container spacing={2}>
                        <Grid size={4}>
                            <TextField
                                fullWidth
                                label="ชื่อ (ไทย)"
                                value={nameTh}
                                onChange={(e) => setNameTh(e.target.value)}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                fullWidth
                                label="ชื่อ (อังกฤษ)"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                fullWidth
                                label="ชื่อ (จีน)"
                                value={nameZh}
                                onChange={(e) => setNameZh(e.target.value)}
                            />
                        </Grid>

                        <Grid size={12}>
                            <Autocomplete
                                multiple
                                options={landmarkOptions}
                                getOptionLabel={(option) => option.nameTh}
                                value={selectedLandmarks}
                                onChange={(_, newValue) => setSelectedLandmarks(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="เลือก Landmarks" placeholder="ค้นหา..." />
                                )}
                            />
                        </Grid>

                        <Grid size={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                type="number"
                                value={latInput}
                                disabled
                                onChange={(e) => setLatInput(e.target.value)}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                type="number"
                                disabled
                                value={lngInput}
                                onChange={(e) => setLngInput(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle1">สิ่งอำนวยความสะดวก</Typography>
                    <FormGroup>
                        <Grid container spacing={1}>
                            {amenityOptions.map((item) => (
                                <Grid size={{ xs: 6, md: 5 }} key={item.key}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!amenities[item.key]}
                                                onChange={() => toggleAmenity(item.key)}
                                            />
                                        }
                                        label={item.label}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    <Box
                        sx={{
                            position: "relative",
                            height: 400,
                            borderRadius: 2,
                            overflow: "hidden",
                        }}
                    >
                        <MapContainer
                            center={markerPos}
                            zoom={16}
                            style={{ height: "100%", width: "100%" }}
                            dragging={!viewOnly}
                            zoomControl={!viewOnly}
                            scrollWheelZoom={!viewOnly}
                            doubleClickZoom={!viewOnly}
                            touchZoom={!viewOnly}
                            boxZoom={!viewOnly}
                            keyboard={!viewOnly}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={markerPos} />
                            {flyTarget && <FlyToLocation position={flyTarget} />}
                            {!viewOnly && (
                                <MapClickHandler
                                    setMarker={(pos) => {
                                        setMarkerPos(pos);
                                        setLatInput(pos[0].toString());
                                        setLngInput(pos[1].toString());
                                    }}
                                />
                            )}
                        </MapContainer>

                        {viewOnly && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    bgcolor: "rgba(255,255,255,0.0)",
                                    cursor: "not-allowed",
                                    zIndex: 10,
                                }}
                            />
                        )}

                        {/* ปุ่ม toggle */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 20,
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                color={viewOnly ? "secondary" : "success"}
                                startIcon={viewOnly ? <Lock /> : <LockOpen />}
                                onClick={() => setViewOnly((v) => !v)}
                            >
                                {viewOnly ? "โหมดดูอย่างเดียว" : "โหมดแก้ไขตำแหน่ง"}
                            </Button>
                        </Box>
                    </Box>

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSubmit}
                        >
                            บันทึก
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Close />}
                            onClick={() => navigate("/stations")}
                        >
                            กลับ
                        </Button>
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
}
